"use client";

import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STORAGE_KEY = "v1_docs_auth";

/**
 * Password gate for `/docs/v1/*` (mirrors the legacy `V1LoginGate` + the
 * `v1_docs_auth` cookie noted in middleware). Against mocks there is no backend
 * password check, so any non-empty passphrase unlocks and the unlock is
 * remembered in localStorage. In production this verifies `V1_DOCS_PASSWORD`.
 */
export function V1Gate({ children }: { children: React.ReactNode }) {
  const [unlocked, setUnlocked] = useState(false);
  const [ready, setReady] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    setUnlocked(window.localStorage.getItem(STORAGE_KEY) === "1");
    setReady(true);
  }, []);

  if (!ready) return null;

  if (!unlocked) {
    return (
      <div className="mx-auto max-w-sm space-y-4 py-16">
        <div>
          <h1 className="text-2xl font-bold">Protected docs</h1>
          <p className="text-sm text-muted-foreground">
            The v1 API reference is password-protected. Enter the passphrase to
            continue.
          </p>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!value.trim()) {
              setError(true);
              return;
            }
            window.localStorage.setItem(STORAGE_KEY, "1");
            setUnlocked(true);
          }}
          className="space-y-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="v1-pass">Passphrase</Label>
            <Input
              id="v1-pass"
              type="password"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError(false);
              }}
              aria-invalid={error}
              // Deliberate: focus the passphrase field on this gate prompt.
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            {error ? (
              <p className="text-xs text-destructive-text">Enter a passphrase.</p>
            ) : null}
          </div>
          <Button type="submit" className="w-full">
            Unlock
          </Button>
        </form>
      </div>
    );
  }

  return <>{children}</>;
}
